import React, { useCallback, useState } from "react"

import { Tooltip } from "@coval/ui"
import { Info } from "lucide-react"

import { JsonCodeEditor } from "@/components/JsonCodeEditor"
import type { MetricRecordData } from "@/types/metrics/contracts"

import {
  FIELD_CONTAINER_STYLES,
  INFO_ICON_STYLES,
  LABEL_ROW_STYLES,
  LABEL_STYLES,
  TEXTAREA_CONTAINER_STYLES,
  TEXTAREA_STYLES,
} from "../styles"
import { HumanReviewInstructionsEditor } from "./HumanReviewInstructionsTable"
import { LLMModelSelect } from "./LLMModelSelect"
import { RequiredMarker } from "./RequiredMarker"
import { TargetFilterToggle } from "./TargetFilterToggle"

interface RubricDecisionTreeConfigProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
}

function JsonTextarea({
  label,
  tooltip,
  value,
  onChange,
  placeholder,
  ariaLabel,
  required = false,
}: {
  label: string
  tooltip: string
  value: unknown
  onChange: (parsed: unknown) => void
  placeholder: string
  ariaLabel: string
  required?: boolean
}) {
  const formatted = value ? JSON.stringify(value, null, 2) : ""
  const [text, setText] = useState(formatted)
  const [error, setError] = useState<string | null>(null)

  const handleChange = useCallback(
    (raw: string) => {
      setText(raw)

      if (!raw.trim()) {
        setError(null)
        onChange(undefined)
        return
      }

      try {
        const parsed: unknown = JSON.parse(raw)
        setError(null)
        onChange(parsed)
      } catch {
        setError("Invalid JSON")
      }
    },
    [onChange],
  )

  return (
    <div className={FIELD_CONTAINER_STYLES}>
      <div className={LABEL_ROW_STYLES}>
        <label className={LABEL_STYLES}>
          {label}
          {required && <RequiredMarker />}
        </label>
        <Tooltip content={tooltip}>
          <Info className={INFO_ICON_STYLES} />
        </Tooltip>
      </div>
      <JsonCodeEditor
        value={text}
        onChange={handleChange}
        placeholder={placeholder}
        ariaLabel={ariaLabel}
        rows={8}
        error={error}
      />
    </div>
  )
}

export const RubricDecisionTreeConfig: React.FC<
  RubricDecisionTreeConfigProps
> = ({ metricRecordData, onChange }) => {
  const prompt = metricRecordData.metric_metadata?.decision_tree_prompt ?? ""

  const updateMetadata = useCallback(
    (updates: Record<string, unknown>) => {
      onChange({
        ...metricRecordData,
        metric_metadata: {
          ...metricRecordData.metric_metadata,
          ...updates,
        },
      })
    },
    [metricRecordData, onChange],
  )

  const handlePromptChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      updateMetadata({ decision_tree_prompt: e.target.value })
    },
    [updateMetadata],
  )

  return (
    <div className="space-y-6">
      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <label className={LABEL_STYLES}>
            Decision Tree Prompt
            <RequiredMarker />
          </label>
          <Tooltip content="The prompt sent to the LLM to evaluate the transcript against the decision tree. Use {transcript} as a placeholder.">
            <Info className={INFO_ICON_STYLES} />
          </Tooltip>
        </div>
        <div className={TEXTAREA_CONTAINER_STYLES}>
          <textarea
            className={TEXTAREA_STYLES}
            value={prompt}
            onChange={handlePromptChange}
            placeholder={`Enter the decision tree evaluation prompt.\n\nUse {transcript} where the conversation transcript should be inserted.\nReference nodes with [NODE_ID: some_id] so the LLM can identify them.`}
            aria-label="Decision tree prompt"
          />
        </div>
      </div>

      <JsonTextarea
        label="Tree Definition"
        required
        tooltip='JSON array of nodes: [{"id": "...", "label": "...", "parent": null, "edge_label": "..."}]'
        value={metricRecordData.metric_metadata?.tree_definition}
        onChange={(parsed) => updateMetadata({ tree_definition: parsed })}
        placeholder={`[\n  {"id": "root", "label": "Start", "parent": null},\n  {"id": "check", "label": "Check Status", "parent": "root", "edge_label": "Yes"}\n]`}
        ariaLabel="Tree definition JSON"
      />

      <JsonTextarea
        label="Scoring Rules"
        tooltip="JSON object mapping outcome names to scores. Leave empty for defaults."
        value={metricRecordData.metric_metadata?.scoring_rules}
        onChange={(parsed) => updateMetadata({ scoring_rules: parsed })}
        placeholder={`{\n  "no_intent_score": 1,\n  "not_triggered_score": 1,\n  "no_failures_score": 5,\n  "non_critical_only_score": 4,\n  "one_critical_score": 2,\n  "multiple_critical_score": 1\n}`}
        ariaLabel="Scoring rules JSON"
      />

      <HumanReviewInstructionsEditor
        value={metricRecordData.metric_metadata?.human_review_instructions}
        onChange={(parsed) =>
          updateMetadata({ human_review_instructions: parsed })
        }
      />

      <LLMModelSelect metricRecordData={metricRecordData} onChange={onChange} />

      <TargetFilterToggle
        metricRecordData={metricRecordData}
        onChange={onChange}
      />
    </div>
  )
}
